import Button from "@/common/components/Button";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities } from "@/common/constants/unities.constants";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { formatDecimal, formatUnit } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useMaintenanceTransportAutoEntriesComplete } from "@/features/maintenance/features/workflows/hooks/forwardings/transport/autoEntries/useMaintenanceTransportAutoEntriesComplete";
import { transportAutoEntriesCompleteSchema, TransportAutoEntriesCompleteValidationSchema } from "@/features/maintenance/features/workflows/schemas/forwardings/transport/autoEntries/transportAutoEntriesComplete.schema";
import { useWorkflowShowStore } from "@/features/maintenance/features/workflows/stores/workflowShowStore";
import { MaintenanceForwardingCheckinEntryTransportAutoEntryData } from "@/features/maintenance/features/workflows/types/forwardings/checkinEntries/transport/autoEntry/transportAutoEntry.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { SignpostIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

interface TransportAutoEntriesCompleteProps {
  checkinEntry: MaintenanceForwardingCheckinEntryTransportAutoEntryData;
  checkType?: 'in' | 'out';
}

const TransportAutoEntriesComplete = ({
  checkinEntry,
  checkType = 'in'
}: TransportAutoEntriesCompleteProps) => {
  const { id } = useParams();

  const [deliveryCode, setDeliveryCode] = useState('');

  const [deliveryCodeModalIsOpen, setDeliveryCodeModalIsOpen] = useState(false);
  const [completeModalIsOpen, setCompleteModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    equipment,
    currentEquipmentUsage,
  } = useWorkflowShowStore();

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const {
    register,
    control,
    reset,
    setError,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm<TransportAutoEntriesCompleteValidationSchema>({
    resolver: zodResolver(transportAutoEntriesCompleteSchema),
    defaultValues: {
      current_equipment_usage: equipment?.last_usage_value
        ? formatUnit(
          formatDecimal(equipment?.last_usage_value, 0),
          equipment?.model?.category?.unit_for_consumption || 'kilometers'
        )
        : undefined,
      observations: ''
    },
  });

  const {
    mutate: transportAutoEntriesComplete,
    isPending: transportAutoEntriesCompleteIsPending,
  } = useMaintenanceTransportAutoEntriesComplete({
    checkinEntryId: checkinEntry.id,
    deliveryCode: deliveryCode,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', id] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflowForwardings', id] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding'] });

        setCompleteModalIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<TransportAutoEntriesCompleteValidationSchema> = (data) => transportAutoEntriesComplete(data);

  useEffect(() => {
    reset({
      current_equipment_usage: equipment?.last_usage_value
        ? formatUnit(
          formatDecimal(equipment?.last_usage_value, 0),
          equipment?.model?.category?.unit_for_consumption || 'kilometers'
        )
        : undefined,
      observations: '',
    });
  }, [currentEquipmentUsage]);

  useEffect(() => {
    if (!completeModalIsOpen) {
      setDeliveryCode('');
    }
  }, [completeModalIsOpen]);

  const checkTypeFormattedText = checkType === 'out' ? 'Check-Out' : 'Check-In'

  return (
    <>
      <Modal
        isOpen={deliveryCodeModalIsOpen}
        setIsOpen={setDeliveryCodeModalIsOpen}
        Trigger={(
          <Button operationKlassToCheckPermission="Establishment::Maintenance::Transport::AutoEntries::Complete">
            <SignpostIcon size={20} />

            Código de {checkTypeFormattedText}
          </Button>
        )}
        title={checkTypeFormattedText}
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        useCancellableFooter
        confirmButtonText={`Confirmar ${checkTypeFormattedText}`}
        confirmButtonClassName="bg-blue-700 disabled:opacity-70 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        confirmButtonDisabled={deliveryCode.length < 4}
        onConfirm={() => {
          setDeliveryCodeModalIsOpen(false);
          setCompleteModalIsOpen(true);
        }}
        onCancel={() => {
          setDeliveryCode('');
        }}
        body={(
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold">
                Solicite o código ao responsável pela coleta do veículo
              </p>

              <p className="text-sm text-gray-400">
                {checkType === 'out'
                  ? 'Peça os 4 dígitos de liberação para a pessoa que está recolhendo o veículo (motorista do guincho ou proprietário). Após a confirmação, será necessário capturar fotos.'
                  : 'Peça os 4 dígitos de liberação para a pessoa que está entregando o veículo (motorista do guincho ou proprietário). Após a confirmação, será necessário capturar fotos.'}
              </p>
            </div>

            <div>
              <MaskedTextField
                mask={['0000']}
                label="Código fornecido pelo responsável pela coleta do veículo"
                markAsRequired
                value={deliveryCode}
                onChange={(event) => setDeliveryCode(event.target.value)}
              />
            </div>
          </div>
        )}
      />

      <Modal
        isOpen={completeModalIsOpen}
        setIsOpen={setCompleteModalIsOpen}
        title="Coleta do equipamento"
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        useCancellableFooter
        className="min-w-[640px]"
        confirmButtonText={`Confirmar ${checkTypeFormattedText}`}
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={transportAutoEntriesCompleteIsPending}
        body={(
          <div className="flex gap-4">
            <MultipleImageUploader
              label="Anexe fotos da coleta"
              name="photos"
              inputClassName="h-[220px] w-[220px] bg-white-200"
              iconClassName="text-white-800"
              accept="image/jpeg, image/png"
              control={control}
              uploadBlob={uploadBlob}
              fieldName="photos"
              uploadButtonLayout="floating"
              isLoading={uploadBlobIsPending}
              disabled={uploadBlobIsPending || transportAutoEntriesCompleteIsPending}
              errorMessage={errors.photos?.message}
              imageCarouselClassName="bg-[#000] h-[220px] w-[220px]"
              imageClassName="object-contain"
            />
            <div className="flex flex-col gap-4 w-full">
              <Controller
                control={control}
                name="current_equipment_usage"
                render={({ field }) => (
                  <UnitTextField
                    label="Odômetro/Horímetro"
                    markAsRequired
                    decimals={0}
                    unit={unities[equipment?.model?.category?.unit_for_consumption || 'kilometers']}
                    valueClassName="h-full flex-1"
                    {...field}
                    errorMessage={errors.current_equipment_usage?.message}
                  />
                )}
              />

              <TextArea
                label={`Observações de ${checkTypeFormattedText}`}
                rows={4}
                {...register('observations')}
                errorMessage={errors.observations?.message}
              />
            </div>
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default TransportAutoEntriesComplete;
